import { useState } from "react";
import { toast } from "sonner";
import { Mail, Users, Calendar } from "lucide-react";
import { submitEnquiry, apiErr } from "../lib/api";
import { trackEvent } from "../lib/analytics";
import { PageHero } from "../components/site/Shared";
import { Reveal } from "../components/site/Reveal";

export default function Contact() {
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", school: "", role: "", interest: "Request a Demo", message: "" });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) { toast.error("Please add your name and email."); return; }
    setLoading(true);
    try {
      await submitEnquiry({ ...form, source: "contact" });
      trackEvent("lead_form_submit", { source: "contact", interest: form.interest });
      setForm({ name: "", email: "", school: "", role: "", interest: "Request a Demo", message: "" });
      toast.success("Thank you — the PARTECON team will be in touch shortly.", { description: "Your enquiry has been received." });
    } catch (err) {
      toast.error(apiErr(err));
    } finally {
      setLoading(false);
    }
  };

  const field = "w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3.5 text-sm text-white placeholder:text-[#9DA9BA]/60 focus:border-[#6366F1]/50 focus:outline-none transition-colors";

  return (
    <>
      <PageHero
        overline="Contact"
        title="Let’s build the future of school operations."
        sub="Whether you’re a school exploring PARTECON, an investor evaluating the opportunity, or a potential strategic partner, we’d be glad to hear from you."
      />

      <section className="pb-28" data-testid="contact-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-[0.8fr_1.2fr] gap-10">
          <Reveal>
            <div className="space-y-5">
              {[
                { icon: Mail, t: "Email", v: "aleem@partecon.com" },
                { icon: Calendar, t: "Product walkthrough", v: "30-minute guided demo" },
                { icon: Users, t: "Who we work with", v: "Schools · Investors · Partners" },
              ].map((c) => (
                <div key={c.t} className="glass rounded-2xl p-6 flex gap-4 items-center">
                  <span className="h-11 w-11 rounded-xl bg-[#6366F1]/10 flex items-center justify-center shrink-0">
                    <c.icon size={20} className="text-[#6366F1]" />
                  </span>
                  <div>
                    <p className="overline">{c.t}</p>
                    <p className="text-white mt-1">{c.v}</p>
                  </div>
                </div>
              ))}
              <a href="mailto:aleem@partecon.com" data-testid="get-in-touch-btn" className="btn-primary px-6 py-3.5 text-sm inline-flex items-center gap-2">Get in Touch</a>
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <form onSubmit={submit} className="glass rounded-3xl p-7 md:p-9 space-y-4" data-testid="contact-form">
              <div className="grid sm:grid-cols-2 gap-4">
                <input data-testid="contact-name" className={field} placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                <input data-testid="contact-email" type="email" className={field} placeholder="Work email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <input data-testid="contact-school" className={field} placeholder="School / organisation" value={form.school} onChange={(e) => setForm({ ...form, school: e.target.value })} />
                <input data-testid="contact-role" className={field} placeholder="Your role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} />
              </div>
              <select data-testid="contact-interest" className={field} value={form.interest} onChange={(e) => setForm({ ...form, interest: e.target.value })}>
                <option className="bg-[#0E1117]">Request a Demo</option>
                <option className="bg-[#0E1117]">Start a Pilot</option>
                <option className="bg-[#0E1117]">Investor / Partnership</option>
                <option className="bg-[#0E1117]">General enquiry</option>
              </select>
              <textarea data-testid="contact-message" className={field} rows={4} placeholder="Tell us about your school and what you'd like to achieve." value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} />
              <button type="submit" disabled={loading} data-testid="contact-submit" className="btn-primary w-full py-4 text-sm disabled:opacity-60">{loading ? "Sending…" : "Send Enquiry"}</button>
            </form>
          </Reveal>
        </div>
      </section>
    </>
  );
}
